/**
 * Fuel Gauge Animation for Vehicle Repossession Form
 * Enhances the fuel level slider with animations and a custom fuel gauge thumb
 */

// Initialize when the DOM is loaded
document.addEventListener("DOMContentLoaded", initFuelGaugeAnimation);

// Function to initialize the fuel gauge animation
function initFuelGaugeAnimation() {
  // Find the fuel gauge slider
  const slider = document.querySelector(".repo-range-input");
  if (!slider) {
    // If slider doesn't exist yet (React might not have rendered it)
    // Set up a mutation observer to detect when it's added to the DOM
    setupMutationObserver();
    return;
  }
  
  // Create and append the custom thumb
  createCustomThumb(slider);
  
  // Add event listeners for animations
  setupSliderAnimations(slider);
}

// Set up mutation observer to detect when the slider is added to the DOM
function setupMutationObserver() {
  const observer = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      if (mutation.type === 'childList') {
        const slider = document.querySelector(".repo-range-input");
        if (slider) {
          observer.disconnect();
          createCustomThumb(slider);
          setupSliderAnimations(slider);
          break;
        }
      }
    }
  });
  
  observer.observe(document.body, {
    childList: true,
    subtree: true
  });
}

// Create the custom thumb with fuel gauge
function createCustomThumb(slider) {
  // Create a container for the custom thumb
  const thumbContainer = document.createElement("div");
  thumbContainer.className = "custom-fuel-thumb-container";
  
  // Make sure the slider's parent has position relative
  slider.parentNode.style.position = "relative";
  slider.parentNode.appendChild(thumbContainer);
  
  // Create the fuel gauge visualization
  const fuelGauge = document.createElement("div");
  fuelGauge.className = "fuel-gauge-thumb";
  
  // Create the fuel level indicator
  const fuelLevel = document.createElement("div");
  fuelLevel.className = "fuel-level-indicator";
  fuelGauge.appendChild(fuelLevel);
  
  // Add a needle indicator
  const needle = document.createElement("div");
  needle.className = "fuel-needle";
  fuelGauge.appendChild(needle);
  
  // Add a small bubble indicator
  const bubble = document.createElement("div");
  bubble.className = "fuel-bubble";
  fuelGauge.appendChild(bubble);
  
  thumbContainer.appendChild(fuelGauge);
  
  // Apply initial position and styles
  updateThumbPosition(slider, thumbContainer, fuelLevel);
  
  // Add the necessary styles to the document
  addCustomStyles();
}

// Set up event listeners for slider animations
function setupSliderAnimations(slider) {
  const thumbContainer = slider.parentNode.querySelector(".custom-fuel-thumb-container");
  const fuelLevel = thumbContainer.querySelector(".fuel-level-indicator");
  
  // Update on input (while dragging)
  slider.addEventListener("input", () => {
    updateThumbPosition(slider, thumbContainer, fuelLevel);
    
    // Add pulse animation
    thumbContainer.classList.add("pulse-animation");
    
    // Create ripple effect
    createRippleEffect(thumbContainer);
    
    // Update the marks with animation
    updateMarksAnimation(slider);
  });
  
  // Remove animations when done sliding
  slider.addEventListener("change", () => {
    setTimeout(() => {
      thumbContainer.classList.remove("pulse-animation");
    }, 300);
  });
  
  // Add hover effect
  slider.addEventListener("mouseover", () => {
    thumbContainer.classList.add("hover-effect");
  });
  
  slider.addEventListener("mouseout", () => {
    thumbContainer.classList.remove("hover-effect");
  });
}

// Update the position and appearance of the custom thumb
function updateThumbPosition(slider, thumbContainer, fuelLevel) {
  // Calculate the position based on slider value
  const value = slider.value;
  const min = slider.min || 0;
  const max = slider.max || 10;
  const percentage = ((value - min) / (max - min)) * 100;
  
  // Position the custom thumb
  const sliderRect = slider.getBoundingClientRect();
  const thumbPosition = (percentage / 100) * (sliderRect.width - 4); // Adjust for thumb width
  
  thumbContainer.style.left = `${thumbPosition}px`;
  
  // Update the fuel level inside the thumb
  fuelLevel.style.height = `${percentage}%`;
  
  // Update color based on fuel level
  updateFuelColor(fuelLevel, percentage);
}

// Update the color of the fuel level based on percentage
function updateFuelColor(fuelLevel, percentage) {
  let color;
  
  if (percentage <= 20) {
    color = "#ef4444"; // Red - low fuel
  } else if (percentage <= 40) {
    color = "#f97316"; // Orange - getting low
  } else if (percentage <= 60) {
    color = "#facc15"; // Yellow - medium
  } else if (percentage <= 80) {
    color = "#84cc16"; // Light green - good
  } else {
    color = "#10b981"; // Green - full
  }
  
  fuelLevel.style.backgroundColor = color;
}

// Create a ripple effect when the slider is moved
function createRippleEffect(thumbContainer) {
  // Create a ripple element
  const ripple = document.createElement("div");
  ripple.className = "fuel-ripple";
  thumbContainer.appendChild(ripple);
  
  // Animate and remove
  setTimeout(() => {
    ripple.remove();
  }, 600);
}

// Animate the marks when the slider value changes
function updateMarksAnimation(slider) {
  const value = parseInt(slider.value);
  const marks = document.querySelectorAll(".repo-gas-mark");
  
  marks.forEach((mark, index) => {
    if (index <= value) {
      mark.classList.add("active");
      mark.classList.add("pop-animation");
      setTimeout(() => {
        mark.classList.remove("pop-animation");
      }, 300 + index * 50);
    } else {
      mark.classList.remove("active");
    }
  });
}

// Add the custom styles to the document
function addCustomStyles() {
  if (!document.getElementById("fuel-gauge-styles")) {
    const styleElement = document.createElement("style");
    styleElement.id = "fuel-gauge-styles";
    
    styleElement.textContent = `
      /* Custom Fuel Gauge Styles */
      .custom-fuel-thumb-container {
        position: absolute;
        top: -28px;
        transform: translateX(-12px);
        width: 24px;
        height: 24px;
        pointer-events: none;
        z-index: 10;
        transition: transform 0.2s ease;
      }
      
      .fuel-gauge-thumb {
        width: 24px;
        height: 24px;
        border-radius: 50%;
        background: white;
        border: 2px solid #3b82f6;
        box-shadow: 0 2px 6px rgba(0, 0, 0, 0.15);
        position: relative;
        overflow: hidden;
      }
      
      .fuel-level-indicator {
        position: absolute;
        bottom: 0;
        left: 0;
        width: 100%;
        height: 0%;
        background-color: #10b981;
        transition: height 0.3s ease, background-color 0.3s ease;
      }
      
      .fuel-needle {
        position: absolute;
        top: 50%;
        left: 50%;
        width: 8px;
        height: 1px;
        background-color: rgba(0, 0, 0, 0.5);
        transform: translate(-50%, -50%);
      }
      
      .fuel-bubble {
        position: absolute;
        bottom: 3px;
        left: 50%;
        width: 4px;
        height: 4px;
        background-color: rgba(255, 255, 255, 0.7);
        border-radius: 50%;
        transform: translateX(-50%);
      }
      
      .pulse-animation {
        animation: fuel-pulse 0.5s ease;
      }
      
      .hover-effect {
        transform: translateX(-12px) scale(1.1);
      }
      
      .fuel-ripple {
        position: absolute;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
        width: 10px;
        height: 10px;
        background-color: rgba(59, 130, 246, 0.3);
        border-radius: 50%;
        animation: fuel-ripple 0.6s ease-out;
      }
      
      .pop-animation {
        animation: fuel-pop 0.3s ease;
      }
      
      /* Animation Keyframes */
      @keyframes fuel-pulse {
        0% { transform: translateX(-12px) scale(1); }
        50% { transform: translateX(-12px) scale(1.2); }
        100% { transform: translateX(-12px) scale(1); }
      }
      
      @keyframes fuel-ripple {
        0% { width: 0; height: 0; opacity: 1; }
        100% { width: 50px; height: 50px; opacity: 0; }
      }
      
      @keyframes fuel-pop {
        0% { transform: scale(1); }
        50% { transform: scale(1.3); }
        100% { transform: scale(1); }
      }
      
      /* Enhanced slider track */
      .repo-range-input {
        height: 8px;
        border-radius: 8px;
        background: linear-gradient(to right, 
          #ef4444 0%, 
          #f97316 20%, 
          #facc15 40%, 
          #84cc16 60%, 
          #22c55e 80%, 
          #10b981 100%);
        outline: none;
        transition: height 0.2s ease;
      }
      
      .repo-range-input:hover {
        height: 10px;
      }
      
      /* Hide the default slider thumb */
      .repo-range-input::-webkit-slider-thumb {
        opacity: 0;
      }
      
      .repo-range-input::-moz-range-thumb {
        opacity: 0;
      }
    `;
    
    document.head.appendChild(styleElement);
  }
}

// Function to manually update the gauge when React state changes
// This can be called from your React component
function updateFuelGauge(value) {
  const slider = document.querySelector(".repo-range-input");
  if (slider) {
    slider.value = value;
    const thumbContainer = slider.parentNode.querySelector(".custom-fuel-thumb-container");
    if (thumbContainer) {
      const fuelLevel = thumbContainer.querySelector(".fuel-level-indicator");
      updateThumbPosition(slider, thumbContainer, fuelLevel);
      updateMarksAnimation(slider);
    } else {
      // If the thumb container doesn't exist yet, initialize it
      createCustomThumb(slider);
      setupSliderAnimations(slider);
    }
  }
}

// Export the function to be used in React
window.updateFuelGauge = updateFuelGauge;

// Function to be called when the component mounts or updates
function initializeFuelGauge() {
  initFuelGaugeAnimation();
}

// Export the initialization function
window.initializeFuelGauge = initializeFuelGauge;